import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme.js';
import type { User } from '../../types/api.js';

type Theme = 'dark' | 'light' | 'system';

interface HeaderProps {
  /** ログインユーザー */
  user: User | null;
  /** ログアウトハンドラ */
  onLogout: () => void;
  /** モバイルメニュートグル */
  onMenuToggle: () => void;
}

/** @graph-connects none */
const THEME_OPTIONS: { value: Theme; icon: string }[] = [
  { value: 'dark', icon: 'moon' },
  { value: 'light', icon: 'sun' },
  { value: 'system', icon: 'monitor' },
];

/** @graph-connects none */
const LANGUAGES = [
  { code: 'ja', label: '日本語' },
  { code: 'en', label: 'English' },
] as const;

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business アプリヘッダー。ロゴ・ユーザードロップダウンメニュー（テーマ・言語・ログアウト）をglass morphismバーで表示
 * @graph-connects useTheme [calls] テーマ管理フック呼び出し
 */
export function Header({ user, onLogout, onMenuToggle }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // メニュー外クリックで閉じる
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideButton = buttonRef.current?.contains(target);
      const isInsideDropdown = dropdownRef.current?.contains(target);
      if (!isInsideButton && !isInsideDropdown) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const currentLangCode = i18n.language.split('-')[0];

  return (
    <>
      <header className="glass-heavy sticky top-0 z-[var(--z-sticky)] h-14">
        <div className="flex items-center justify-between h-full px-4">
          {/* 左: ハンバーガー + ロゴ */}
          <div className="flex items-center gap-3">
            <button
              onClick={onMenuToggle}
              className="lg:hidden p-2 rounded-lg hover:bg-[var(--color-bg-hover)] transition-colors"
              aria-label="Toggle menu"
            >
              <svg
                className="w-5 h-5 text-[var(--color-text-secondary)]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-gradient">Mall Scorer</span>
            </div>
          </div>

          {/* 右: ユーザーメニューボタン */}
          {user && (
            <div ref={buttonRef}>
              <button
                onClick={() => {
                  setIsMenuOpen((prev) => !prev);
                }}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-[var(--color-bg-hover)] transition-colors"
                aria-expanded={isMenuOpen}
                aria-haspopup="true"
              >
                <span className="hidden sm:block text-sm font-medium text-[var(--color-text-primary)]">
                  {user.name}
                </span>
                <svg
                  className={`w-4 h-4 text-[var(--color-text-muted)] transition-transform ${
                    isMenuOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ドロップダウンメニュー — ヘッダーの外に配置してbackdrop-filterを有効化 */}
      {isMenuOpen && user && (
        <div
          ref={dropdownRef}
          className="fixed top-16 right-4 w-64 z-[var(--z-modal)] rounded-xl animate-fade-in border border-[var(--color-glass-border)]"
          style={{
            background: 'var(--color-glass-bg)',
            backdropFilter: 'blur(12px) saturate(1.4)',
            WebkitBackdropFilter: 'blur(12px) saturate(1.4)',
            boxShadow: 'var(--shadow-xl), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
          }}
        >
          {/* ユーザー情報 */}
          <div className="px-4 py-3 border-b border-[var(--color-border)]">
            <p className="text-sm font-medium text-[var(--color-text-primary)]">{user.name}</p>
            <p className="text-xs text-[var(--color-text-muted)] truncate">{user.email}</p>
          </div>

          {/* テーマ切替 */}
          <div className="px-4 py-3 border-b border-[var(--color-border)]">
            <p className="text-xs font-semibold text-[var(--color-text-muted)] mb-2">
              {t('settings.themeTitle')}
            </p>
            <div className="flex gap-1">
              {THEME_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    setTheme(opt.value);
                  }}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    theme === opt.value
                      ? 'bg-[var(--color-accent-muted)] text-[var(--color-accent-primary)] border border-[var(--color-accent-primary)]/20'
                      : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
                  }`}
                >
                  {t(`settings.${opt.value}`)}
                </button>
              ))}
            </div>
          </div>

          {/* 言語切替 */}
          <div className="px-4 py-3 border-b border-[var(--color-border)]">
            <p className="text-xs font-semibold text-[var(--color-text-muted)] mb-2">
              {t('settings.languageTitle')}
            </p>
            <div className="flex gap-1">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    void i18n.changeLanguage(lang.code);
                  }}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    lang.code === currentLangCode
                      ? 'bg-[var(--color-accent-muted)] text-[var(--color-accent-primary)] border border-[var(--color-accent-primary)]/20'
                      : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* ログアウト */}
          <button
            onClick={() => {
              setIsMenuOpen(false);
              onLogout();
            }}
            className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-error)] hover:bg-[var(--color-bg-hover)] transition-colors rounded-b-xl"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            {t('settings.logout')}
          </button>
        </div>
      )}
    </>
  );
}
